import React from "react";
import styles from "./EmployeeDetailPanel.module.css";

const fmtMoney = (n) =>
  (isFinite(n) ? n : 0).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

/**
 * props:
 * - employeeName: string (optional – for title)
 * - rows: [{
 *     code: "04776-001",
 *     name: "Trident at Shelby Shores, LLC",
 *     roles: { cp: true, cm: false, sp: true, sm: false }  // or array of labels if you prefer
 *     amounts: { cp: 0, cm: 0, sp: 440, sm: 0, total: 440 }
 *   }, ...]
 * - onDownloadCSV?: () => void
 */
export default function EmployeeDetailPanel({
  employeeName,
  rows = [],
  onDownloadCSV,
  iconURL,
  onManualReassign,
  isRowElective,
  wipByClientCode
}) {


  function ClientWipHover({ clientCode, clientName, wipRows = [] }) {
    if (!wipRows.length) return <span>{clientName}</span>;

    // normalize + sort
    const rows = [...wipRows]
      .map(r => ({
        employee: r.employee ?? r.EMPLOYEE ?? r.Employee ?? "",
        level: r.level ?? r.LEVEL ?? r.Level ?? "",
        dept: r.department ?? r.DEPARTMENT ?? r.Department ?? "",
        wipHours: Number(r.wiphours ?? r.WIPHOURS ?? r.WipHours ?? 0),
      }))
      .filter(x => x.employee);

    rows.sort((a, b) => b.wipHours - a.wipHours);

    const total = rows.reduce((n, x) => n + (x.wipHours || 0), 0);

    return (
      <span className="rwz-client-hover">
        {clientName}
        <div className="rwz-hover-card" style={{ minWidth: 320, maxWidth: 520 }}>
          <div className="rwz-hover-title">
            {clientCode} — {clientName}
          </div>

          {/* header */}
          <div className="rwz-hover-row rwz-hover-row--wiphead">
            <span className="rwz-hover-wipcol employee">Employee</span>
            <span className="rwz-hover-wipcol level">Level</span>
            <span className="rwz-hover-wipcol dept">Dept</span>
            <span className="rwz-hover-wipcol hours">Wip Hours</span>
          </div>

          {/* body */}
          {rows.map((x, idx) => (
            <div key={idx} className="rwz-hover-row rwz-hover-row--wip">
              <span className="rwz-hover-wipcol employee">{x.employee}</span>
              <span className="rwz-hover-wipcol level">{x.level}</span>
              <span className="rwz-hover-wipcol dept">{x.dept}</span>
              <span className="rwz-hover-wipcol hours">{x.wipHours.toFixed(2)}</span>
            </div>
          ))}

          {/* total */}
          <div className="rwz-hover-row rwz-hover-row--wiptotal">
            <span className="rwz-hover-wipcol employee"><strong>Total</strong></span>
            <span className="rwz-hover-wipcol level" />
            <span className="rwz-hover-wipcol dept" />
            <span className="rwz-hover-wipcol hours"><strong>{total.toFixed(2)}</strong></span>
          </div>
        </div>
      </span>
    );
  }

  return (
    <section className={styles.panel} aria-label="Employee managed billings detail">
      <header className={styles.header}>
        <h3 className={styles.title}>
          Client Detail{employeeName ? ` for ${employeeName}` : ""}
        </h3>
        <button
          type="button"
          className={styles.action}
          onClick={onDownloadCSV}
          aria-label="Download client detail as CSV"
        >
          Download CSV
        </button>
      </header>

      {/* Band over CP..SM */}
      <div className={styles.bandRow}>
        <div className={styles.bandFiller} />
        <div className={styles.bandFiller} />
        <div className={styles.bandFiller} />
        <div className={styles.bandFiller} />
        <div className={styles.band}>Managed Billings by Role</div>
        {/* total column intentionally not covered by the band */}
      </div>

      {/* Column heads */}
      <div className={styles.grid}>
        <div className={styles.thCenter}>Manual Reassignment</div>
        <div className={styles.th}>Client Code</div>
        <div className={styles.th}>Client Name</div>
        <div className={styles.th}>Roles</div>
        <div className={styles.thRight}>Client Partner</div>
        <div className={styles.thRight}>Client Manager</div>
        <div className={styles.thRight}>Service Partner</div>
        <div className={styles.thRight}>Service Manager</div>
        <div className={styles.thRight}>Total</div>

        {/* Data rows */}
        {rows.map((r, i) => {
          const highlighted = isRowElective?.(r.code, r.roleChips || r.roles);
          const rowClass = highlighted ? styles.rowElective : "";
        
        
          return (
            <React.Fragment key={`${r.code}-${i}`}>
              {/* Manual reassignment button cell */}
              <div className={`${styles.td} ${styles.manualCell} ${rowClass}`}>
                <button
                  type="button"
                  className={styles.manualBtn}
                  // onClick={() => onManualReassign && onManualReassign({
                  //   clientCode: r.code,
                  //   assignments: r.roleChips.map(r => ({
                  //     role: r.roleChips.kind,         // "CM", "SM", etc.
                  //     service: r.roleChips.tag,   // maybe undefined for CM/CP
                  //   })),
                  // })}
                  onClick={() => {
                    if (!onManualReassign) return;

                    const assignments = (r.roleChips || []).map(chip => ({
                      role: chip.kind,        // "CM", "SM", etc.
                      service: chip.tag,      // service code, "" for CP/CM
                    }));

                    onManualReassign({
                      clientCode: r.code,
                      assignments,
                    });
                  }}
                  aria-label={`Start manual reassignment for ${r.name || r.code}`}
                  title={"Manually create reassignment task(s) for the given role(s) and client. Click again to remove task(s)."}
                >
                  {iconURL ? (
                    <img src={iconURL} alt="" className={styles.manualIcon} />
                  ) : (
                    <span className={styles.manualFallback}>➔</span>
                  )}
                </button>
              </div>
              <div className={`${styles.td} ${styles.mono}`}>{r.code}</div>
              {/* <div className={styles.td}>{r.name}</div> */}
              <div className={styles.td}>
                <ClientWipHover
                  clientCode={r.code}
                  clientName={r.name}
                  wipRows={wipByClientCode?.get(r.code) || []}
                />
              </div>
              <div className={`${styles.td} ${styles.roles}`}>{renderRoles(r.roleChips || r.roles)}</div>
              <div className={`${styles.td} ${styles.num}`}>{fmtMoney(r.amounts?.cp)}</div>
              <div className={`${styles.td} ${styles.num}`}>{fmtMoney(r.amounts?.cm)}</div>
              <div className={`${styles.td} ${styles.num}`}>{fmtMoney(r.amounts?.sp)}</div>
              <div className={`${styles.td} ${styles.num}`}>{fmtMoney(r.amounts?.sm)}</div>
              <div className={`${styles.td} ${styles.num} ${styles.total}`}>
                {fmtMoney(r.amounts?.total ??
                  (r.amounts?.cp || 0) + (r.amounts?.cm || 0) + (r.amounts?.sp || 0) + (r.amounts?.sm || 0))}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </section>
  )}


function renderRoles(rolesOrChips) {
  // NEW preferred shape: array of { kind: "CP"|"CM"|"SP"|"SM", tag: string, text: string }
  if (Array.isArray(rolesOrChips)) {
    const classKey = { CP: "cp", CM: "cm", SP: "sp", SM: "sm" };
    return rolesOrChips.map((c, i) => (
      <span key={`${c.kind}-${c.tag}-${i}`} className={`${styles.chip} ${styles[`chip_${classKey[c.kind]}`]}`}>
        {c.text}
      </span>
    ));
  }

  // Back-compat: previous boolean map { cp, cm, sp, sm }
  if (rolesOrChips && typeof rolesOrChips === "object") {
    const chips = Object.entries(rolesOrChips)
      .filter(([, v]) => !!v)
      .map(([k]) => k.toUpperCase());
    const label = { CP: "Client Partner", CM: "Client Manager", SP: "Service Partner", SM: "Service Manager" };
    const classKey = { CP: "cp", CM: "cm", SP: "sp", SM: "sm" };
    return chips.map((k, idx) => (
      <span key={`${k}-${idx}`} className={`${styles.chip} ${styles[`chip_${classKey[k]}`]}`}>
        {label[k]}
      </span>
    ));
  }

  return null;
}